import {
  usePatchExercisePreset,
  usePostExercisePreset
} from '#api/exercise-presets';
import { queryKeyFactory } from '#api/queryKeyFactory';
import { ButtonSubmit } from '#pages/WeeklyCalenderScheduleRegistration/styles';
import { useToast } from '#providers/ToastProvider';
import { useExercisePresetRecoil } from '#states/exercisePresetsState';
import { IExercisePresetWorkoutSet } from '#types/exercisePresets';
import { useQueryClient } from '@tanstack/react-query';
import TagManager from 'react-gtm-module';

interface SubmitButtonProps {
  onClose: () => void;
  id?: string;
}

/**
 * 프리셋 추가 버튼
 */
export const SubmitButton = ({ onClose, id }: SubmitButtonProps) => {
  const queryClient = useQueryClient();

  // recoil에 저장된 추가할 프리셋 정보 가져오기
  const { exercisePresets, setExercisePresets } = useExercisePresetRecoil();

  const { showToast } = useToast();

  const buttonText = id ? '수정' : '추가';

  // preset 추가 api
  const { mutate: addPresets } = usePostExercisePreset({
    onSuccess: () => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'exercisechart_presetadd_add_click',
          category: '회원관리',
          action: 'exercisechart_presetadd_add_click',
          value: 2
        }
      });
      queryClient.invalidateQueries(queryKeyFactory.GET_EXERCISE_PRESETS());
      onClose();
      showToast({
        type: 'success',
        message: `프리셋이 ${buttonText}되었습니다.`
      });
    },
    onError: () => {
      showToast({
        type: 'fail',
        message: `프리셋 ${buttonText}에 실패했습니다.`
      });
    }
  });

  // preset 수정 api
  const { mutate: editPresets } = usePatchExercisePreset({
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeyFactory.GET_EXERCISE_PRESETS());
      setExercisePresets({
        name: '',
        exercisePresetWorkout: [
          {
            exerciseTypeId: '',
            sets: [
              {
                weight: 0,
                reps: 0,
                time: 0,
                setType: 'REPWEIGHT',
                round: 0
              }
            ]
          }
        ]
      });

      onClose();
      showToast({
        type: 'success',
        message: `프리셋이 ${buttonText}되었습니다.`
      });
    },
    onError: () => {
      showToast({
        type: 'fail',
        message: `프리셋 ${buttonText}에 실패했습니다.`
      });
    }
  });

  // 기입 방식에 따른 값이 모두 입력되었는지 확인
  const checkSet = (set: IExercisePresetWorkoutSet) => {
    const { setType, reps, time, weight } = set;

    if (reps && setType === 'REP') {
      return reps > 0;
    }

    if (time && setType === 'TIME') {
      return time > 0;
    }

    if (reps && time && setType === 'REPTIME') {
      return reps > 0 && time > 0;
    }

    if (weight && reps && setType === 'REPWEIGHT') {
      return weight > 0 && reps > 0;
    }

    return false;
  };

  const disabled =
    exercisePresets?.name === '' ||
    exercisePresets?.exercisePresetWorkout?.every(
      exercisePresetsWorkout =>
        exercisePresetsWorkout?.exerciseTypeId === '' ||
        exercisePresetsWorkout?.sets?.every(set => !checkSet(set))
    );

  // 프리셋 추가
  const onSubmit = () => {
    if (id) {
      editPresets({ id, params: { ...exercisePresets, id } });
      return;
    }

    addPresets(exercisePresets);

    onClose();
  };

  return (
    <ButtonSubmit disabled={disabled} onClick={onSubmit}>
      {buttonText}
    </ButtonSubmit>
  );
};
